import React from "react";

export const num = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

export const money = (value) =>
  num(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// The four states a plan moves through. `severity` feeds PrimeReact tags,
// `tone` feeds the bp-pill classes used outside the datatable.
export const STATUS_META = {
  draft: { label: "Draft", tone: "muted", severity: "info" },
  submitted: { label: "Submitted", tone: "pending", severity: "warning" },
  approved: { label: "Approved", tone: "ok", severity: "success" },
  rejected: { label: "Rejected", tone: "alert", severity: "danger" },
};

export const statusMeta = (status) =>
  STATUS_META[status] || { label: status || "—", tone: "muted", severity: "info" };

export const StatusPill = ({ status }) => {
  const meta = statusMeta(status);
  return <span className={`bp-pill bp-pill-${meta.tone}`}>{meta.label}</span>;
};

// A plan's spend against its budget. `consumed` is what journal vouchers have
// booked, so the bar shows how much headroom is left before an overrun.
export const UtilisationBar = ({ budget, spent }) => {
  const total = num(budget);
  const used = num(spent);
  const pct = total > 0 ? (used / total) * 100 : 0;
  const capped = Math.min(Math.max(pct, 0), 100);
  const over = total > 0 && used > total;

  return (
    <div className="bp-bar-wrap">
      <div className="bp-bar">
        <div
          className={`bp-bar-fill${over ? " is-over" : ""}${
            !over && capped > 85 ? " is-near" : ""
          }`}
          style={{ width: `${capped}%` }}
        />
      </div>
      <span className={`bp-bar-label${over ? " is-over" : ""}`}>
        {total > 0 ? `${pct.toFixed(1)}%` : "—"}
      </span>
    </div>
  );
};

// Shared look for every Budget Plan screen, in the same house style the
// journal voucher and cash count screens use.
export const BudgetPlanStyles = () => (
  <style>
    {`
      .bp-panel {
        background: #fff;
        border: 1px solid #e6e8f0;
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
        margin-bottom: 20px;
      }
      .bp-panel-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 20px;
        border-bottom: 1px solid #e6e8f0;
        font-size: 0.95rem;
        font-weight: 600;
        color: #2a2962;
      }
      .bp-head-note {
        font-size: 0.75rem;
        font-weight: 500;
        color: #8b90a5;
      }
      .bp-panel-body { padding: 20px; }
      .bp-panel label {
        display: block;
        margin-bottom: 6px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #6b7280;
      }
      .bp-panel .form-control:focus {
        border-color: #2a2962;
        box-shadow: 0 0 0 0.2rem rgba(42, 41, 98, 0.12);
      }
      .bp-panel .required { color: #e42320; font-size: 0.5rem; vertical-align: super; }

      .bp-num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }

      /* Stat strip */
      .bp-stat {
        position: relative;
        background: #fff;
        border: 1px solid #e6e8f0;
        border-left: 3px solid #2a2962;
        border-radius: 8px;
        padding: 14px 16px;
        height: 100%;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
      }
      .bp-stat.is-alert { border-left-color: #e42320; }
      .bp-stat.is-ok { border-left-color: #1f9d55; }
      .bp-stat.is-pending { border-left-color: #c77700; }
      .bp-stat-label {
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #8b90a5;
        margin-bottom: 4px;
      }
      .bp-stat-value {
        font-size: 1.25rem;
        font-weight: 700;
        color: #2a2962;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }
      .bp-stat.is-alert .bp-stat-value { color: #e42320; }
      .bp-stat.is-ok .bp-stat-value { color: #1f9d55; }
      .bp-stat.is-pending .bp-stat-value { color: #c77700; }
      .bp-stat-sub {
        display: block;
        margin-top: 2px;
        font-size: 0.72rem;
        color: #b6bacb;
      }
      .bp-stat-icon {
        position: absolute;
        top: 14px;
        right: 16px;
        font-size: 1.1rem;
        color: #e6e8f0;
      }

      /* Line-item table */
      .bp-line-table { width: 100%; margin-bottom: 0; }
      .bp-line-table thead th {
        padding: 10px 12px;
        background: #2a2962;
        color: #fff;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border: 0;
        white-space: nowrap;
      }
      .bp-line-table tbody td {
        padding: 8px 12px;
        border-bottom: 1px solid #f0f1f6;
        vertical-align: top;
      }
      .bp-line-table tbody tr:hover { background: #fafbfd; }
      .bp-line-table .form-control,
      .bp-line-table .select__control { font-size: 0.86rem; }
      .bp-line-table tfoot th,
      .bp-line-table tfoot td {
        padding: 11px 12px;
        background: #f2f3f9;
        border: 0;
        border-top: 2px solid #2a2962;
        font-size: 0.95rem;
        font-weight: 700;
        color: #2a2962;
      }
      .bp-row-index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #f2f3f9;
        color: #6b7280;
        font-size: 0.75rem;
        font-weight: 600;
      }
      .bp-cell-note {
        display: block;
        margin-top: 4px;
        font-size: 0.72rem;
        color: #8b90a5;
      }
      .bp-cell-note.is-over { color: #e42320; font-weight: 600; }

      /* Read-only key/value rows */
      .bp-kv {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 16px;
        padding: 9px 0;
        border-bottom: 1px solid #f0f1f6;
      }
      .bp-kv:last-child { border-bottom: 0; }
      .bp-kv-label {
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8b90a5;
      }
      .bp-kv-value {
        font-size: 0.9rem;
        font-weight: 600;
        color: #35374d;
        text-align: right;
      }

      /* Status pills */
      .bp-pill {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 12px;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      .bp-pill-muted { background: #eef0f8; color: #6b7280; }
      .bp-pill-pending { background: #fdf3e2; color: #c77700; }
      .bp-pill-ok { background: #e8f6ee; color: #1f9d55; }
      .bp-pill-alert { background: #fdecec; color: #e42320; }

      /* Utilisation bar */
      .bp-bar-wrap { display: flex; align-items: center; gap: 8px; }
      .bp-bar {
        flex: 1;
        min-width: 60px;
        height: 6px;
        border-radius: 3px;
        background: #eef0f8;
        overflow: hidden;
      }
      .bp-bar-fill {
        height: 100%;
        border-radius: 3px;
        background: #2a2962;
        transition: width 0.2s ease;
      }
      .bp-bar-fill.is-near { background: #c77700; }
      .bp-bar-fill.is-over { background: #e42320; }
      .bp-bar-label {
        font-size: 0.72rem;
        font-weight: 600;
        color: #6b7280;
        font-variant-numeric: tabular-nums;
        min-width: 44px;
        text-align: right;
      }
      .bp-bar-label.is-over { color: #e42320; }

      /* Inline notices */
      .bp-notice {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px 16px;
        border-radius: 8px;
        border: 1px solid #e6e8f0;
        border-left: 3px solid #2a2962;
        background: #f7f8fc;
        font-size: 0.85rem;
        color: #52566b;
        margin-bottom: 20px;
      }
      .bp-notice.is-locked { border-left-color: #c77700; background: #fdf9f2; }
      .bp-notice i { color: #8b90a5; margin-top: 2px; }

      .bp-empty {
        padding: 48px 20px;
        text-align: center;
        color: #8b90a5;
      }
      .bp-empty i { font-size: 2rem; color: #d7dae8; display: block; margin-bottom: 10px; }

      .bp-total-box { border: 1px solid #e6e8f0; border-radius: 8px; overflow: hidden; }
      .bp-total-row {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        padding: 10px 16px;
        border-bottom: 1px solid #f0f1f6;
        font-size: 0.85rem;
        color: #6b7280;
      }
      .bp-total-row span:last-child {
        font-weight: 600;
        color: #35374d;
        font-variant-numeric: tabular-nums;
      }
      .bp-total-row.bp-total-net {
        border-bottom: 0;
        background: #2a2962;
        padding: 14px 16px;
      }
      .bp-total-row.bp-total-net span { color: #fff; }
      .bp-total-row.bp-total-net span:last-child {
        font-size: 1.15rem;
        font-weight: 700;
      }

      @media print {
        .bp-no-print { display: none !important; }
        .bp-panel { box-shadow: none; break-inside: avoid; }
      }
    `}
  </style>
);
